import type { RouteObject } from 'react-router'
import type { SchoolModuleKey } from '@/features/school/modules'
import type { NavItem } from '@/layouts/navigation'

/**
 * Contract for a feature module plugging into the app without editing shared files.
 * Each file in src/routes/modules/*.tsx exports `module: AppModule`.
 */
export interface AppModule {
  /** Stable id, e.g. "attendance". */
  id: string
  /** The optional school module this belongs to: its pages and menu items disappear when the school has it off. */
  schoolModule?: SchoolModuleKey
  /** Routes mounted under /admin, /staff and /portal (paths relative to that prefix). */
  routes?: { admin?: RouteObject[]; staff?: RouteObject[]; family?: RouteObject[] }
  /** Navigation contributions. `group` must be one of the group labels in navigation.ts. */
  nav?: {
    admin?: { group: AdminNavGroup; order: number; item: NavItem }[]
    staff?: { order: number; item: NavItem }[]
    family?: { order: number; item: NavItem }[]
  }
}

export type AdminNavGroup =
  | 'Overview'
  | 'People'
  | 'Academics'
  | 'Operations'
  | 'Finance'
  | 'Communication'
  | 'Resources'
  | 'Administration'

const loaded = import.meta.glob<{ module: AppModule }>('./modules/*.tsx', { eager: true })

export const appModules: AppModule[] = Object.values(loaded)
  .map((m) => m.module)
  .sort((a, b) => a.id.localeCompare(b.id))
